import React, { useState, useEffect } from 'react';
import { RiSearchLine, RiCloseLine, RiFilterLine, RiFilterOffLine } from 'react-icons/ri';
import SearchableSelect from './SearchableSelect';
import ActiveFilterTags, { FilterTag } from './ActiveFilterTags';

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'searchable-select' | 'checkbox' | 'date';
  options?: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  /**
   * type 'select' เท่านั้น: ไม่ใส่ตัวเลือกว่าง ("ทั้งหมด") ด้านบน — สำหรับ select ที่ต้องมีค่าเสมอ
   * เช่น "เรียงตาม"/"ลำดับ" (E2E UI-B B-02) · ค่าเริ่มต้น false = พฤติกรรมเดิมของทุกหน้า
   */
  hideEmptyOption?: boolean;
  searchPlaceholder?: string;
  tagColor?: 'secondary' | 'primary' | 'success' | 'warning' | 'accent' | 'amber' | 'danger' | 'info';
  // Alternative pattern: value and onChange directly in filter config
  value?: string | number | boolean;
  onChange?: (value: string) => void;
}

interface SearchFilterCardProps {
  // Search
  searchTerm?: string;
  /** Alias for searchTerm (backward compat) */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  showSearch?: boolean;

  // Filters (optional)
  filters?: FilterConfig[];
  filterValues?: Record<string, any>;
  onFilterChange?: (key: string, value: any) => void;

  // Clear
  onClearAll?: () => void;
  /** Alias for onClearAll (backward compat) */
  onClearFilters?: () => void;
  showClearButton?: boolean;

  // Title
  title?: string;
  showTitle?: boolean;

  // Active filter tags
  showFilterTags?: boolean;

  // Custom buttons
  customButtons?: React.ReactNode;

  // Layout
  className?: string;
}

const SearchFilterCard: React.FC<SearchFilterCardProps> = ({
  searchTerm: searchTermProp = '',
  searchValue,
  onSearchChange,
  searchPlaceholder = 'ค้นหา...',
  showSearch = true,
  filters = [],
  filterValues = {},
  onFilterChange,
  onClearAll,
  onClearFilters,
  showClearButton = true,
  title = 'ค้นหาและกรองข้อมูล',
  showTitle = true,
  showFilterTags = true,
  customButtons,
  className = '',
}) => {
  // Support both searchTerm and searchValue aliases
  const searchTerm = searchValue !== undefined ? searchValue : searchTermProp;
  // Local state for debounced search input
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Sync external searchTerm → local state (e.g. when parent clears search)
  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  // Debounce local → external: fire onSearchChange 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        onSearchChange?.(localSearchTerm);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  // Check if any filter is active
  const hasActiveFilters = localSearchTerm ||
    Object.values(filterValues).some(v => v !== '' && v !== null && v !== undefined && v !== false) ||
    filters.some(f => f.value !== '' && f.value !== null && f.value !== undefined && f.value !== false);

  // Generate filter tags for display
  const generateFilterTags = (): FilterTag[] => {
    const tags: FilterTag[] = [];

    // Search tag
    if (localSearchTerm) {
      tags.push({
        key: 'search',
        label: 'ค้นหา',
        value: localSearchTerm,
        color: 'secondary',
      });
    }

    // Filter tags
    filters.forEach((filter) => {
      // Support both patterns: filter.value or filterValues[filter.key]
      const value = filter.value !== undefined ? filter.value : filterValues[filter.key];
      if (value !== '' && value !== null && value !== undefined && value !== false) {
        let displayValue = String(value);

        // Get label from options if available
        if (filter.options) {
          const option = filter.options.find(opt => String(opt.value) === String(value));
          if (option) {
            displayValue = option.label;
          }
        }

        // For checkbox type
        if (filter.type === 'checkbox' && value === true) {
          displayValue = 'เปิด';
        }

        tags.push({
          key: filter.key,
          label: filter.label,
          value: displayValue,
          color: (filter.tagColor || 'primary') as any,
        });
      }
    });

    return tags;
  };

  const handleRemoveTag = (key: string) => {
    if (key === 'search') {
      setLocalSearchTerm('');
      onSearchChange?.('');
    } else {
      const filter = filters.find(f => f.key === key);
      if (filter) {
        const clearValue = filter.type === 'checkbox' ? 'false' : '';
        if (filter.onChange) {
          filter.onChange(clearValue);
        } else if (onFilterChange) {
          onFilterChange(key, filter.type === 'checkbox' ? false : '');
        }
      }
    }
  };

  const handleClearAll = () => {
    setLocalSearchTerm('');
    onSearchChange?.('');
    filters.forEach(filter => {
      const clearValue = filter.type === 'checkbox' ? 'false' : '';
      if (filter.onChange) {
        filter.onChange(clearValue);
      } else if (onFilterChange) {
        onFilterChange(filter.key, filter.type === 'checkbox' ? false : '');
      }
    });
    onClearAll?.();
    onClearFilters?.();
  };

  // BUG-S2015-002 (user report 2026-09-02): ห้าม overflow-hidden บนการ์ด — dropdown ของ SearchableSelect
  // (absolute z-[9999]) ถูกการ์ดตัดทิ้งแล้วโดนตารางด้านล่างบัง คลิก option ไม่ถึง
  // (devices/device-models/authorized-signers/contract-tracking) · ลูกทั้งหมดอยู่ใน p-3.5 ไม่มี background
  // เต็มขอบ → มุมโค้งไม่เพี้ยนเมื่อ overflow-visible
  return (
    <div className={`mb-4 bg-white rounded-xl shadow-sm border border-secondary-200 overflow-visible ${className}`}>
      <div className="p-3.5">
      {/* Title */}
      {showTitle && (
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
            <RiFilterLine className="w-3.5 h-3.5 text-secondary-500" />
          </div>
          <h2 className="text-sm font-semibold text-secondary-700">{title}</h2>
        </div>
      )}

      {/* Search Input */}
      {showSearch && (
        <div className="relative mb-3">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
          <input
            type="text"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="w-full pl-9 pr-9 h-11 sm:h-9 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-400 bg-secondary-50/50 hover:bg-white hover:border-secondary-300 transition-all placeholder:text-secondary-400"
          />
          {localSearchTerm && (
            <button
              onClick={() => { setLocalSearchTerm(''); onSearchChange?.(''); }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors p-0.5 rounded-full hover:bg-secondary-100"
              aria-label="ล้างการค้นหา"
            >
              <RiCloseLine className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Filters Row */}
      {filters.length > 0 && (
        <div className="flex flex-wrap items-end gap-2.5">
          {filters.map((filter) => (
            <div key={filter.key} className="flex-1 min-w-[140px] max-w-[240px]">
              <label className="block text-xs font-medium text-secondary-500 mb-1">
                {filter.label}
              </label>
              {filter.type === 'select' && (
                <select
                  value={filter.value !== undefined ? filter.value : (filterValues[filter.key] || '')}
                  onChange={(e) => {
                    if (filter.onChange) {
                      filter.onChange(e.target.value);
                    } else {
                      onFilterChange?.(filter.key, e.target.value);
                    }
                  }}
                  aria-label={filter.label}
                  className="w-full h-11 sm:h-9 px-3 border border-secondary-200 rounded-lg text-sm bg-secondary-50/50 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-400 transition-all text-secondary-700"
                >
                  {!filter.hideEmptyOption && (
                    <option value="">{filter.placeholder || 'ทั้งหมด'}</option>
                  )}
                  {/* R235: กรอง option ที่ value ว่างออก — component render placeholder "ทั้งหมด"
                      (value="") ให้เสมอด้านบนแล้ว หลาย consumer ยังส่ง { label:'ทั้งหมด', value:'' }
                      มาใน options ด้วย → เกิด <option value=""> ซ้ำสองตัวในทุก filter select. กรองที่
                      component เดียว ครอบ consumer ทั้งหมด (แทนการไล่ลบทีละหน้า). */}
                  {filter.options?.filter((option) => String(option.value) !== '').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
              {filter.type === 'date' && (
                <input
                  type="date"
                  value={filter.value !== undefined ? String(filter.value) : (filterValues[filter.key] || '')}
                  onChange={(e) => {
                    if (filter.onChange) {
                      filter.onChange(e.target.value);
                    } else {
                      onFilterChange?.(filter.key, e.target.value);
                    }
                  }}
                  aria-label={filter.label}
                  className="w-full h-11 sm:h-9 px-3 border border-secondary-200 rounded-lg text-sm bg-secondary-50/50 hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-400 transition-all"
                />
              )}
              {filter.type === 'searchable-select' && (
                <SearchableSelect
                  options={filter.options || []}
                  value={filter.value !== undefined ? String(filter.value) : String(filterValues[filter.key] || '')}
                  onChange={(value) => {
                    const strVal = value != null ? String(value) : '';
                    if (filter.onChange) {
                      filter.onChange(strVal);
                    } else {
                      onFilterChange?.(filter.key, strVal);
                    }
                  }}
                  placeholder={filter.placeholder || 'ทั้งหมด'}
                  label={filter.label}
                  optionValue="value"
                  optionLabel="label"
                />
              )}
              {filter.type === 'checkbox' && (
                <label className="flex items-center h-[42px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filter.value !== undefined ? Boolean(filter.value) : (filterValues[filter.key] || false)}
                    onChange={(e) => {
                      if (filter.onChange) {
                        filter.onChange(String(e.target.checked));
                      } else {
                        onFilterChange?.(filter.key, e.target.checked);
                      }
                    }}
                    className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-secondary-700">
                    {filter.placeholder || filter.label}
                  </span>
                </label>
              )}
            </div>
          ))}

          {/* Clear Filters Button */}
          {showClearButton && hasActiveFilters && (
            <button
              onClick={handleClearAll}
              className="inline-flex items-center h-11 sm:h-9 px-3 text-sm text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors gap-1.5 border border-secondary-200 hover:border-secondary-300"
            >
              <RiFilterOffLine className="w-3.5 h-3.5" />
              ล้าง
            </button>
          )}

          {/* Custom Buttons */}
          {customButtons}
        </div>
      )}

      {/* Clear button when no filters but has search */}
      {filters.length === 0 && showClearButton && localSearchTerm && (
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={handleClearAll}
            className="inline-flex items-center px-3 py-1.5 text-sm text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors gap-1.5"
          >
            <RiFilterOffLine className="w-3.5 h-3.5" />
            ล้างการค้นหา
          </button>
          {customButtons}
        </div>
      )}

      {/* Active Filter Tags */}
      {showFilterTags && (
        <ActiveFilterTags
          tags={generateFilterTags()}
          onRemove={handleRemoveTag}
        />
      )}
      </div>
    </div>
  );
};

export default SearchFilterCard;
